import { useRef, useEffect } from 'react'

//
// useAnimationFrame Hook
//
// To use this Hook, your component should manage a local state variable to control whether the animation should run. This is essential because
// the animation will continue if the component is unmounted, allowing it to persist if the user navigates away and returns.
// However, it's crucial to stop the animation on unmount to prevent unnecessary resource usage. The cleanup function in useEffect cancels the
// animation frame to handle this.
//
// Example setup for using the useAnimationFrame hook in a component:
//
// const [shouldAnimate, setShouldAnimate] = useState(false)
// useEffect(() => {
//   // Update local state when freeScrolling changes so the animation hook can start or stop accordingly
//   setShouldAnimate(freeScrolling)
// }, [freeScrolling])
//
// // Define the tick function that will execute on each animation frame
// const nextAnimationFrameHandler = () => {
//   // Implement animation logic here
//   // Keep in mind that this function may encounter stale closures; if accessing state or props, use refs for reliable access
// }

type UseAnimationFrame = (nextAnimationFrameHandler: () => void, shouldAnimate?: boolean) => void

const useAnimationFrame: UseAnimationFrame = (nextAnimationFrameHandler, shouldAnimate = true) => {
  const frame = useRef<number>(0)

  const animate = () => {
    nextAnimationFrameHandler()
    frame.current = requestAnimationFrame(animate)
  }

  useEffect(() => {
    if (shouldAnimate) {
      frame.current = requestAnimationFrame(animate)
    } else {
      cancelAnimationFrame(frame.current)
    }

    return () => cancelAnimationFrame(frame.current)
  }, [shouldAnimate])
}

export default useAnimationFrame
